import React from "react";
import ExclamationIcon from "../../assets/exclamation.svg";
import { COLUMN_WIDTHS } from "../../helpers/common/ag-grid-helper";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";

export const createPassViewColumns = (t) => {
  const baseColumnConfig = {
    pivot: true,
    floatingFilter: true,
    filter: "agMultiColumnFilter",
    filterParams: {
      maxNumConditions: 1,
    },
    pivotComparator: (a, b) => b.localeCompare(a),
  };

  function resolveNested(obj, path) {
    return path.split(".").reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  const valueRender = (params) => {
    const text = params.value;
    if (text && typeof text === "string" && text.includes("-color")) {
      return (
        <div>
          <p className="red-note-text">
            <span className="red-note">
              {Number(text.replace("-color", ""))}
            </span>
          </p>
        </div>
      );
    }
    return isNaN(text) ? "" : Number(text);
  };

  const unitsCommonFilter = {
    pivot: true,
    floatingFilter: true,
    filter: "agMultiColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      filters: [
        {
          filter: "agNumberColumnFilter",
          filterParams: {
            valueFormatter: (params) => {
              return Number(
                params.value && typeof params.value === "string"
                  ? params.value.replace("-color", "")
                  : params.value || 0
              );
            },
            maxNumConditions: 1,
          },
        },
      ],
    },
    valueGetter: (params) => {
      const value = resolveNested(params.data, params.colDef.field);
      return Number(
        value && typeof value === "string"
          ? value.replace("-color", "")
          : value || 0
      );
    },
    processCellForExport: (params) => {
      return params.value && typeof params.value === "string"
        ? params.value.replace("-color", "")
        : params.value;
    },
    pivotComparator: (a, b) => b.localeCompare(a),
    cellRenderer: (params) => {
      const value = resolveNested(params.data, params.colDef.field);

      return valueRender({
        value: value,
      });
    },
  };

  const createBasicColumn = (
    field,
    headerKey,
    width = COLUMN_WIDTHS.medium
  ) => ({
    ...baseColumnConfig,
    field,
    headerName: t(headerKey),
    key: field,
    width,
  });

  const createMeasurementColumns = (headerKey, fieldPrefix, keyPrefix) => ({
    headerName: t(headerKey),
    key: keyPrefix,
    children: [
      {
        field: `${fieldPrefix}.low`,
        headerName: t("slogColumntitle.low"),
        key: `${keyPrefix}_low`,
        ...unitsCommonFilter,
      },
      {
        field: `${fieldPrefix}.avg`,
        headerName: t("slogColumntitle.avg"),
        key: `${keyPrefix}_avg`,
        ...unitsCommonFilter,
      },
      {
        field: `${fieldPrefix}.high`,
        headerName: t("slogColumntitle.high"),
        key: `${keyPrefix}_high`,
        ...unitsCommonFilter,
      },
    ],
  });

  return [
    createBasicColumn("sl_no", "slogColumntitle.sl.no", COLUMN_WIDTHS.small),
    {
      ...createBasicColumn(
        "status",
        "slogColumntitle.status",
        COLUMN_WIDTHS.small
      ),
      cellRenderer: (params) =>
        params.value
          ? logDataHelperFunctions.getIconForStatus(params.value)
          : "",
      processCellForExport: (params) =>
        params.value
          ? logDataHelperFunctions.getExportValueForStatus(
              params.value?.toLowerCase()
            )
          : "",
      filterParams: {
        filters: [
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellHeight: 40,
              cellRenderer: (params) => {
                const status = String(params.value);
                if (status === "(Select All)") {
                  return "(Select All)";
                }
                return logDataHelperFunctions.getIconForStatus(status);
              },
              valueFormatter: (params) => {
                return String(params.value);
              },
            },
          },
        ],
        maxNumConditions: 1,
      },
    },
    createBasicColumn("stationNumbers", "slogColumntitle.stationName"),
    createBasicColumn("buyType", "slogColumntitle.buyType"),
    createBasicColumn("type", "slogColumntitle.torch"),
    {
      ...createBasicColumn("name", "slogColumntitle.pass"),
      cellRenderer: (params) =>
        params.value === "!" ? (
          <img src={ExclamationIcon} alt="Exclamation" />
        ) : (
          params.value
        ),
    },
    {
      ...createBasicColumn("rawWeldStartTime", "slogColumntitle.weldstartTime"),
      cellRenderer: (params) =>
        Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTimeLive(params.value, true)
          : "-",
      processCellForExport: (params) =>
        Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTimeLive(params.value, true)
          : "-",
      filterParams: {
        maxNumConditions: 1,
        filters: [
          {
            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue) =>
                aggridHelpers.dateFilter(filterLocalDateAtMidnight, cellValue),
              maxNumConditions: 1,
              inRangeInclusive: true,
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: (params) => {
                const value = String(params.value);
                if (value === "(Select All)") {
                  return "(Select All)";
                }
                return Number(params.value) > 0
                  ? logDataHelperFunctions.formatWeldStartTimeLive(
                      params.value,
                      true
                    )
                  : "-";
              },
              valueFormatter: (params) => {
                return String(params.value);
              },
              comparator: (filterLocalDateAtMidnight, cellValue) =>
                aggridHelpers.dateFilter(filterLocalDateAtMidnight, cellValue),
            },
          },
        ],
      },
    },
    {
      ...createBasicColumn(
        "weld_time",
        "slogColumntitle.weldTime",
        COLUMN_WIDTHS.large
      ),
      filterParams: {
        maxNumConditions: 1,
        filters: [
          {
            filter: "agNumberColumnFilter",
            filterParams: { maxNumConditions: 1 },
          },
        ],
      },
      valueGetter: (params) => {
        const value = resolveNested(params.data, params.colDef.field);
        return isNaN(value) ? value : Number(value);
      },
    },
    createBasicColumn("autoIncrement", "slogColumntitle.autoIncrement"),
    createMeasurementColumns("slogColumntitle.current", "amps", "currentA"),
    createMeasurementColumns("slogColumntitle.volt", "volts", "voltV"),
    createMeasurementColumns(
      "slogColumntitle.travelSpeed",
      "travel",
      "travelSpeed"
    ),
    createMeasurementColumns(
      "slogColumntitle.trueEnergy",
      "heat",
      "trueEnergy"
    ),
    createMeasurementColumns("slogColumntitle.heat", "calculated", "heat"),
    createMeasurementColumns("slogColumntitle.wireSpeed", "wire", "wireSpeed"),
    createMeasurementColumns(
      "slogColumntitle.oscillationWidth",
      "oscillate",
      "oscillationWidth"
    ),
    createMeasurementColumns(
      "slogColumntitle.frequency",
      "frequency",
      "frequency"
    ),
    createMeasurementColumns("slogColumntitle.target", "target", "target"),
    {
      ...createMeasurementColumns(
        "slogColumntitle.horizontalBias",
        "horizontal",
        "horizontalBias"
      ),
      position: 18,
    },
    {
      ...createMeasurementColumns(
        "slogColumntitle.totalWireConsumed",
        "total",
        "totalWireConsumed"
      ),
      position: 19,
    },
    createMeasurementColumns(
      "slogColumntitle.arcImpedance",
      "arcImpedance",
      "arcImpedance"
    ),
  ];
};
